
// API Base URLs
export const API_BASE_V2 = 'https://ostdata.gprlive.com/api/v2';
export const IMAGE_BASE = 'https://ostdata.gprlive.com';

// Helper function for time formatting
function timeAgo(iso) {
  try {
    const t = new Date(iso);
    const diff = Math.floor((Date.now() - t.getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;

    const days = Math.floor(diff / 86400);
    if (days < 30) return `${days}d ago`;

    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;

    const years = Math.floor(months / 12);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  } catch (e) {
    return 'recently';
  }
}

// Home page API endpoints
const HOME_ENDPOINTS = {
  HOME_DATA: '/home',
};

// ✅ Helper: Get token from cookies or localStorage
function getAuthToken() {
  // 1) Check common window globals that some apps expose
  try {
    if (typeof window !== 'undefined') {
      if (window.__AUTH_TOKEN__) return String(window.__AUTH_TOKEN__);
      if (window.__auth_token__) return String(window.__auth_token__);
      if (window.APP && window.APP.authToken) return String(window.APP.authToken);
      if (window.__INITIAL_STATE__?.auth?.token) return String(window.__INITIAL_STATE__.auth.token);
      if (window.__INITIAL_STATE__?.user?.token) return String(window.__INITIAL_STATE__.user.token);
    }
  } catch (e) {
    /* ignore window checks on server or restricted env */
  }

  // 2) Check several localStorage keys (many apps use one of these)
  const lsKeys = ['auth_token', 'authToken', 'token', 'access_token', 'accessToken', 'jwt'];
  try {
    for (const k of lsKeys) {
      const v = localStorage.getItem(k);
      if (v) return String(v);
    }
  } catch (e) {
    // localStorage may be disabled in some environments
  }

  // 3) Check cookies for common token cookie names
  try {
    if (typeof document !== 'undefined' && document.cookie) {
      const cookieNames = ['auth_token', 'authToken', 'token', 'access_token', 'accessToken', 'laravel_token', 'jwt'];
      for (const name of cookieNames) {
        const m = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
        if (m && m[2]) return decodeURIComponent(m[2]);
      }
    }
  } catch (e) {
    // ignore cookie read errors
  }

  // 4) Nothing found
  return null;
}

// ✅ Check if user is authenticated
function isUserAuthenticated() {
  return !!getAuthToken();
}

// API functions for home page
export const homeApi = {
  // ✅ Fetch home page data (with or without token)
  getHomePageData: async () => {
    try {
      const token = getAuthToken(); // 👈 get token if exists

      const headers = {
        'Content-Type': 'application/json',
      };

      // 👇 if token exists, add Authorization header
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_V2}${HOME_ENDPOINTS.HOME_DATA}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch home page data');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching home page data:', error);
      throw error;
    }
  },

  // ✅ Fetch quiz list with filters and pagination
  fetchQuizList: async (params = {}) => {
    const token = getAuthToken();

    const headers = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token exists (for user-specific data)
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Build query string
    const queryParams = new URLSearchParams();

    // Add pagination
    queryParams.append('page', params.page || 1);
    queryParams.append('limit', params.limit || 9);

    // Add filters if provided
    // Use 'class' parameter name (server expects alias value), not legacy 'gc'
    if (params.class) queryParams.append('class', params.class);
    if (params.subject) queryParams.append('subject', params.subject);
    if (params.category) queryParams.append('category', params.category);
    if (params.type !== undefined) queryParams.append('type', params.type);
    if (params.difficulty) queryParams.append('difficulty', params.difficulty);
    if (params.attempted !== undefined) queryParams.append('attempted', params.attempted);
    if (params.search) queryParams.append('search', params.search);

    const url = `${API_BASE_V2}/quiz-list?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch quiz list');
    }

    const data = await response.json();
    return data;
  },

  // --- unchanged below ---

  transformQuizData: (quiz) => {
    const gradients = [
      'from-yellow-50 to-orange-50',
      'from-green-50 to-teal-50',
      'from-blue-50 to-indigo-50',
      'from-pink-50 to-purple-50',
      'from-red-50 to-pink-50',
      'from-cyan-50 to-blue-50',
      'from-emerald-50 to-lime-50',
      'from-rose-50 to-fuchsia-50',
    ];

    const colors = [
      'yellow', 'green', 'blue', 'pink', 'red', 'cyan', 'emerald', 'purple'
    ];

    const idx = quiz.id % gradients.length;

    return {
      id: quiz.id,
      alias: quiz.alias || quiz.slug || null,
      type: quiz.quiz_type || 'other',
      title: quiz.title,
      subtitle: quiz.description ? quiz.description.slice(0, 80).replace(/(<([^>]+)>)/gi, '') : '',
      duration: quiz.duration_minutes ? `${quiz.duration_minutes} min` : (quiz.duration ? `${quiz.duration}` : '—'),
      questions: quiz.total_questions || quiz.questions || 0,
      likes_count: typeof quiz.likes_count === 'number' ? quiz.likes_count : (quiz.likes || 0),
      attempts_count: typeof quiz.attempts_count === 'number' ? quiz.attempts_count : (quiz.attempts || 0),
      participants_count: typeof quiz.participants_count === 'number' ? quiz.participants_count : (quiz.participants || 0),
      user_liked: quiz.user_liked ?? quiz.userLiked ?? false,
      bookmarked: quiz.bookmarked ?? false,
      stats: [
        { label: 'Like', value: (typeof quiz.likes_count === 'number' ? quiz.likes_count : (quiz.likes || 0)), colorClass: 'text-yellow-500', liked: quiz.user_liked ?? false },
        { label: 'Attempts', value: (typeof quiz.attempts_count === 'number' ? quiz.attempts_count : (quiz.attempts || 0)), colorClass: 'text-blue-600' },
        { label: 'Participants', value: (typeof quiz.participants_count === 'number' ? quiz.participants_count : (quiz.participants || 0)), colorClass: 'text-green-600' },
      ],
      description: quiz.description || '',
      difficulty_level: quiz.difficulty || 'Mixed',
      updated_at: quiz.updated_at,
      class: quiz.class || (quiz.subject && quiz.subject.class) || null,
      class_subject: quiz.class_subject || (quiz.subject ? {
        title: quiz.subject.name || quiz.subject.title || (typeof quiz.subject === 'string' ? quiz.subject : ''),
        // Always map subject.colour to color for both subject and class_subject
        color: quiz.subject?.color || quiz.subject?.colour || undefined,
        alias: quiz.subject?.alias
      } : null),
      // Also ensure subject.color is set for direct access
      subject: quiz.subject ? {
        ...quiz.subject,
        color: quiz.subject?.color || quiz.subject?.colour || undefined
      } : undefined,
      class_subject_alias: quiz.class_subject?.alias || quiz.class_subject_alias || quiz.subject?.alias || null,
      topPerformer: (() => {
        // Prefer explicit top_performers array, then `topper` object (API sample), then participant_avatars
        const tp = (quiz.top_performers && quiz.top_performers[0]) || quiz.topper || (quiz.participant_avatars && quiz.participant_avatars[0]) || null;

        // Helper to derive a friendly name
        const deriveName = (t) => {
          if (!t) return 'Top Performer'
          if (t.name) {
            // Some APIs provide `name` and `last_name`
            if (t.last_name && !t.name.includes(t.last_name)) return `${t.name} ${t.last_name}`.trim()
            return t.name
          }
          if (t.first_name) return `${t.first_name} ${t.last_name || ''}`.trim()
          return t.user_name || t.username || 'Top Performer'
        }

        const img = tp ? (tp.profile_picture || tp.profile_image || tp.avatar) : null

        // Expose user_id and user_name for profile redirection
        return {
          name: deriveName(tp),
          designation: '',
          username: tp ? (tp.user_name || tp.username || '') : '',
          user_id: tp ? (tp.user_id || tp.id || tp.userid || tp.userId || null) : null,
          score: tp ? (tp.best_score_percentage || tp.score || tp.best_score || 'NA') : 'NA',
          best_score_percentage: tp ? (tp.best_score_percentage || tp.score || tp.best_score || 'NA') : 'NA',
          city: tp ? (tp.city || '') : '',
          state: tp ? (tp.state || '') : '',
          img: img ? (img.startsWith('http') ? img : `${IMAGE_BASE}${img.startsWith('/') ? '' : '/'}${img}`) : `${IMAGE_BASE}/images/online.png`,
          gradientClass: gradients[idx],
          borderClass: `border-${colors[idx]}-400 ring-2 ring-${colors[idx]}-100`,
          scoreColorClass: `text-${colors[idx]}-600`,
          scoreTextColorClass: `text-${colors[idx]}-700`,
        };
      })(),
      // Map avatars array (API) to participant_avatars, normalizing objects and URLs.
      // Ensure each avatar object includes: { user_id, username, profile_image, name, city, state, best_score_percentage }
      // Preserve items like { extra_count: N } so the UI can show "+N more".
      participant_avatars: (() => {
        const src = Array.isArray(quiz.participant_avatars) ? quiz.participant_avatars : (Array.isArray(quiz.avatars) ? quiz.avatars : []);

        const mapAvatar = (a) => {
          if (!a) return null;

          // String URL -> normalize to absolute URL
          if (typeof a === 'string') {
            return a.startsWith('http') ? a : `${IMAGE_BASE}${a.startsWith('/') ? '' : '/'}${a}`;
          }

          // Preserve explicit extra_count objects (e.g. { extra_count: 550 })
          if (a.extra_count !== undefined) return { extra_count: a.extra_count };

          // If it's already an object with normalized fields, map gracefully
          const img = a.profile_image || a.profile_picture || a.avatar || a.image || null;
          const normalizedImg = img ? (typeof img === 'string' && img.startsWith('http') ? img : `${IMAGE_BASE}${img.startsWith('/') ? '' : '/'}${img}`) : null;

          return {
            user_id: a.user_id ?? a.id ?? a.userId ?? null,
            username: a.username ?? a.user_name ?? a.userName ?? a.user_name ?? null,
            profile_image: normalizedImg,
            name: a.name ?? a.display_name ?? a.user_name ?? a.username ?? '',
            city: a.city ?? '',
            state: a.state ?? '',
            institution: a.institution ?? '',
            institution_name: a.institution_name ?? a.institution ?? '',
            best_score_percentage: a.best_score_percentage ?? a.score_percentage ?? a.score ?? 'NA',
          };
        };

        return src.map(mapAvatar);
      })(),
      // Map extra_count from either field
      extra_count: typeof quiz.extra_count === 'number' ? quiz.extra_count : (quiz.extra_count || quiz.extraCount || 0),
      // Map top_performers array with all relevant data (city, state, score, etc.)
      top_performers: (() => {
        const src = Array.isArray(quiz.top_performers) ? quiz.top_performers : [];
        const mapTopPerformer = (tp) => {
          if (!tp) return null;
          const img = tp.profile_picture || tp.profile_image || tp.avatar || null;
          const normalizedImg = img ? (img.startsWith('http') ? img : `${IMAGE_BASE}${img.startsWith('/') ? '' : '/'}${img}`) : null;

          return {
            user_id: tp.user_id ?? tp.id ?? null,
            username: tp.username ?? tp.user_name ?? null,
            name: tp.name ?? tp.display_name ?? '',
            profile_image: normalizedImg,
            city: tp.city ?? '',
            state: tp.state ?? '',
            institution: tp.institution ?? '',
            institution_name: tp.institution_name ?? tp.institution ?? '',
            best_score_percentage: tp.best_score_percentage ?? tp.score_percentage ?? tp.score ?? 'NA',
            score: tp.score ?? tp.best_score ?? 'NA',
            full_score: tp.full_score ?? tp.max_score ?? null,
            attempts: tp.attempts ?? 0,
            average_score: tp.average_score ?? null,
          };
        };
        return src.map(mapTopPerformer).filter(tp => tp !== null);
      })(),
      tags: [
        { label: quiz.difficulty || 'Mixed', bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
      ],
      attempted: quiz.attempted ?? false,
      updated: timeAgo(quiz.updated_at),
      updated_iso: quiz.updated_at ? new Date(quiz.updated_at).toISOString() : null,
      updated_exact: quiz.updated_at ? new Date(quiz.updated_at).toLocaleString() : null,
      buttonBg: 'bg-gradient-to-r from-blue-500 to-purple-600 text-white',
      gradientClass: 'bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-700',
    };
  },

  transformResourceData: (resource) => {
    // Normalize and fallback for image URL
    const imageUrl = resource.image || resource.image_url || resource.featured_image || `${IMAGE_BASE}/storage/images/online.png`;
    const normalizedImage = imageUrl.startsWith('http') ? imageUrl : `${IMAGE_BASE}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;

    // Helper to coerce various API shapes into booleans/numbers
    const parseBool = (v) => {
      if (v === true || v === 1 || v === '1') return true
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase()
        return s === 'true' || s === '1'
      }
      return false
    }

    const parseNumberOr = (v, fallback = 0) => {
      if (typeof v === 'number' && Number.isFinite(v)) return v
      if (v === null || typeof v === 'undefined' || v === '') return fallback
      const n = Number(v)
      return Number.isFinite(n) ? n : fallback
    }

    return {
      // ✅ Basic Info
      id: resource.id,
      title: resource.title || 'Untitled',
      description: resource.description || '',
      subtitle: resource.description ? resource.description.slice(0, 150).replace(/(<([^>]+)>)/gi, '') : '',

      // ✅ Image & Type
      image: normalizedImage,
      type: resource.type || 'resource',

      // ✅ Subject Info (with color fallback)
      subject: {
        name: resource.subject?.name || resource.subject?.title || 'Subject',
        color: resource.subject?.color || resource.subject?.colour || '#6366f1',
        icon: resource.subject?.icon || 'fas fa-book-open'
      },

      // ✅ Class & Grade Info
      grade: resource.grade || resource.class || null,
      class_alias: resource.class_alias || null,
      class_subject_alias: resource.class_subject_alias || null,
      class_subject_title: resource.class_subject_title || 'N/A',

      // ✅ Slug & Navigation
      slug: resource.slug || resource.alias || String(resource.id),
      alias: resource.alias || String(resource.id),

      // ✅ Engagement Metrics (coerce types for consistency)
      likes_count: parseNumberOr(resource.likes_count ?? resource.likes ?? 0, 0),
      user_liked: parseBool(resource.user_liked ?? resource.userLiked ?? false),
      bookmarked: parseBool(resource.bookmarked ?? false),
      tests_count: typeof resource.tests_count === 'number' ? resource.tests_count : (resource.tests || 0),
      students_count: typeof resource.students_count === 'number' ? resource.students_count : (resource.students || 0),
      discussions_count: typeof resource.discussions_count === 'number' ? resource.discussions_count : (resource.discussions || 0),

      // ✅ Timestamps & Time Formatting
      updated_at: resource.updated_at || new Date().toISOString(),
      updated_text: resource.updated_text || (resource.updated_at ? timeAgo(resource.updated_at) : 'recently'),
      updated: timeAgo(resource.updated_at || new Date().toISOString()),
      updated_iso: resource.updated_at ? new Date(resource.updated_at).toISOString() : null,
      updated_exact: resource.updated_at ? new Date(resource.updated_at).toLocaleString() : null,

      // ✅ Preserve raw JSON data field when present (some APIs return `json_data` as a string)
      json_data: resource.json_data || resource.jsonData || resource.json || null,
      // ✅ Parsed version (attempt to parse JSON string into object for convenience)
      json_data_parsed: (() => {
        try {
          const raw = resource.json_data || resource.jsonData || resource.json || null
          if (!raw) return null
          return typeof raw === 'string' ? JSON.parse(raw) : raw
        } catch (e) {
          return null
        }
      })(),
      // ✅ Action Buttons
      buttons: [
        { text: 'Learn', icon: 'fas fa-book', title: 'Start Learning' },
        { text: 'Practice', icon: 'fa-solid fa-trophy', title: 'Take Assessment' },
      ],
      // Preserve original participant information when present so UI components can render avatars/counts
      participants: resource.participants ?? resource.participant_avatars ?? resource.participants_list ?? null,
      participant_avatars: resource.participant_avatars ?? resource.avatars ?? null,
      // Preserve extra/total participant counts under several keys for compatibility (coerce when possible)
      extra_participants_count: (typeof resource.extra_participants_count === 'number') ? resource.extra_participants_count : (resource.extra_participants_count ? Number(resource.extra_participants_count) : (resource.extra_count ? Number(resource.extra_count) : (resource.extraCount ? Number(resource.extraCount) : null))),
      total_participants: (typeof resource.total_participants === 'number') ? resource.total_participants : (resource.total_participants ? Number(resource.total_participants) : (resource.total ? Number(resource.total) : null)),
      // ✅ Preserve study sections data (section completion tracking)
      study: Array.isArray(resource.study) ? resource.study : null,
      // ✅ PRESERVE CLASS, SUBJECT, CLASS_SUBJECT, AND CATEGORY OBJECTS FROM API
      class: resource.class || null,
      class_subject: resource.class_subject || null,
      category: resource.category || null,
      // Keep raw original object for advanced fallbacks in components
      _raw: resource,
    };
  },

  transformActivityData: (activity) => {
    // Extract user information
    const user = activity.user || {};
    const userName = user.name || user.display_name || user.user_name || 'Unknown User';
    const userImage = user.profile_picture || user.profile_image || user.avatar || '';
    const normalizedImage = userImage ? (userImage.startsWith('http') ? userImage : `${IMAGE_BASE}${userImage.startsWith('/') ? '' : '/'}${userImage}`) : `${IMAGE_BASE}/images/online.png`;

    // Determine action text based on activity type
    let actionText = '';
    let activityType = activity.type || 'upload';
    
    if (activity.type === 'upload') {
      const title = activity.title || 'uploaded content';
      actionText = `  ${title}`;
    } else if (activity.type === 'post') {
      actionText = ' started a discussion';
    } else if (activity.type === 'comment') {
      actionText = ' added a comment';
    } else if (activity.type === 'question') {
      actionText = ' added a question';
    } else {
      actionText = ` ${activity.type}d content`;
    }

    return {
      id: activity.id || Math.random(),
      type: activityType,
      userName: userName,
      name: userName,
      userImage: normalizedImage,
      img: normalizedImage,
      action: actionText,
      uploadTitle: activity.upload_title || activity.details?.upload_title || '',
      message: activity.title || activity.details?.title || '',
      details: activity.details?.body || activity.details?.description || '',
      title: activity.title || '',
      time: timeAgo(activity.created_at || new Date().toISOString()),
      created_at: activity.created_at,
      user_id: user.id || activity.user_id,
      user: user,
      // Icon mapping based on type
      icon: (() => {
        const iconMap = {
          'upload': 'fas fa-upload',
          'post': 'fas fa-comment-dots',
          'comment': 'fas fa-comment',
          'question': 'fas fa-question-circle',
          'video': 'fas fa-video',
          'link': 'fas fa-link'
        };
        return iconMap[activityType] || 'fas fa-plus-circle';
      })(),
      // Badge color mapping
      iconBg: (() => {
        const bgMap = {
          'upload': 'bg-blue-100',
          'post': 'bg-purple-100',
          'comment': 'bg-green-100',
          'question': 'bg-orange-100',
          'video': 'bg-red-100',
          'link': 'bg-indigo-100'
        };
        return bgMap[activityType] || 'bg-gray-100';
      })(),
    };
  },
};

// ✅ Export helper functions for use in components
// Fetch resources (v2) — token-aware, paginated, normalized
export async function fetchResources(params = {}) {
  try {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('✅ Sending token to /resource-list:', `Bearer ${token.substring(0, 20)}...`);
    } else {
      console.warn('⚠️ No token found for /resource-list request');
    }

    // Build query (server expects `page` and `limit`)
    const qp = new URLSearchParams();
    qp.append('page', params.page || 1);
    // prefer explicit params.limit, fall back to params.per_page (legacy), default to 9
    const limitVal = params.limit ?? params.per_page ?? 9;
    qp.append('limit', limitVal);
    if (params.search) qp.append('search', params.search);
    if (params.subject) qp.append('subject', params.subject);
    if (params.class) qp.append('class', params.class);
    if (params.category) qp.append('category', params.category);

    const url = `${API_BASE_V2}/resource-list?${qp.toString()}`;

    const resp = await fetch(url, { method: 'GET', headers });
    if (!resp.ok) throw new Error(`Failed to fetch resources: ${resp.status}`);

    const payload = await resp.json();

    // Items can be in various shapes — try common locations
    let items = [];
    if (Array.isArray(payload.data)) items = payload.data;
    else if (payload.data && Array.isArray(payload.data.resources)) items = payload.data.resources;
    else if (Array.isArray(payload.resources)) items = payload.resources;
    else if (Array.isArray(payload.items)) items = payload.items;

    const transform = homeApi && typeof homeApi.transformResourceData === 'function'
      ? homeApi.transformResourceData
      : (r) => r;

    const transformed = items.map((it) => transform(it));

    // Extract pagination from proper location
    const paginationSource = payload.pagination || payload.meta || {};
    const meta = {
      last_page: paginationSource.last_page || paginationSource.total_pages || paginationSource.totalPages || 1,
      total: paginationSource.total_records || paginationSource.total || transformed.length,
      current_page: paginationSource.current_page || paginationSource.currentPage || params.page || 1,
      // Map server limit/per_page to `per_page` for backward compatibility
      per_page: (paginationSource.per_page || paginationSource.perPage || paginationSource.limit) ||
        (params.limit !== undefined ? params.limit : (params.per_page !== undefined ? params.per_page : 9)),
      has_next_page: paginationSource.has_next_page || false,
    };

    // ✅ Extract filters from API response (top-level or nested in data)
    const classes = payload.classes || payload.data?.classes || [];
    const subjects = payload.subjects || payload.data?.subjects || [];
    const categories = payload.categories || payload.data?.categories || [];

    return {
      ...payload,
      data: transformed,
      pagination: meta,
      meta,
      // ✅ Preserve classes, subjects, and categories from API response for filter dropdowns
      classes,
      subjects,
      categories,
    };
  } catch (err) {
    console.error('apiv2.fetchResources error:', err);
    throw err;
  }
}

export async function fetchParticularResource(params = {}) {
  try {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('✅ Sending token to /resource:', `Bearer ${token.substring(0, 20)}...`);
    } else {
      console.warn('⚠️ No token found for /resource request');
    }

    // Validate required parameters
    if (!params.alias) {
      throw new Error('Alias parameter is required for fetching a particular resource');
    }
    if (!params.class) {
      throw new Error('Class parameter is required for fetching a particular resource');
    }
    if (!params.subject) {
      throw new Error('Subject parameter is required for fetching a particular resource');
    }

    // Build query parameters
    const qp = new URLSearchParams();
    qp.append('alias', params.alias);
    qp.append('c', params.class);
    qp.append('s', params.subject);
    
    // Optional parameters
    if (params.uid) qp.append('uid', params.uid);
    if (params.cp) qp.append('cp', params.cp); // chapter
    // Optional 'q' param for fetching questions (e.g., q=question)
    if (params.q) qp.append('q', params.q);

    const url = `${API_BASE_V2}/resource?${qp.toString()}`;

    const resp = await fetch(url, { method: 'GET', headers });
    if (!resp.ok) throw new Error(`Failed to fetch resource: ${resp.status}`);

    const payload = await resp.json();

    // Normalize response structure
    if (!payload.success) {
      throw new Error(payload.message || 'Failed to fetch resource');
    }

    // Extract resource data - handle both single object and array responses
    let resourceData = null;
    if (Array.isArray(payload.data) && payload.data.length > 0) {
      resourceData = payload.data[0]; // Take first item from array
    } else if (payload.data && typeof payload.data === 'object') {
      resourceData = payload.data; // Direct object
    } else {
      // Some API calls (e.g., when querying only questions with `q=question`) return
      // `qanswer` and other fields but omit the full `data` resource object. In such
      // cases, do not throw; return an empty resource object and let callers use
      // `qanswer`, `progressstates`, etc.
      if (payload.qanswer || params.q === 'question' || payload.q === 'question') {
        console.warn('apiv2.fetchParticularResource: no `data` in response, but qanswer present - continuing with empty resource object');
        resourceData = {};
      } else {
        throw new Error('No resource data found in response');
      }
    }

    // Apply transformation if available
    const transform = homeApi && typeof homeApi.transformResourceData === 'function'
      ? homeApi.transformResourceData
      : (r) => r;

    const transformedResource = transform(resourceData);

    // Extract additional data from response
    const additionalData = {
      quizProgress: payload.Qz_group_quiz_progress || null,
      questionAnswers: payload.qanswer || {},
      allChapters: payload.all_chapters || [],
      pagination: {
        current_page: 1,
        total_pages: payload.pages || 1,
        total: 1
      }
    };

    // Normalize related_chapters (API may return in several locations)
    const rawRelated = payload.related_chapters || resourceData.related_chapters || (Array.isArray(payload.data) && payload.data[0] && payload.data[0].related_chapters) || [];
    const mapChapter = (c) => {
      if (!c) return null;
      const img = c.image || c.chapter_image || c.image_url || c.featured_image || null;
      const normalizedImg = img ? (typeof img === 'string' && img.startsWith('http') ? img : `${IMAGE_BASE}${img.startsWith('/') ? '' : '/'}${img}`) : `${IMAGE_BASE}/storage/images/online.png`;
      return {
        id: c.id ?? null,
        title: c.title ?? c.chapter_title ?? '',
        alias: c.alias ?? c.chapter_alias ?? String(c.id ?? ''),
        chapter_id: c.chapter_id ?? c.chapterId ?? null,
        chapter_title: c.chapter_title ?? c.title ?? '',
        chapter_alias: c.chapter_alias ?? c.alias ?? '',
        chapter_description: c.chapter_description ?? c.description ?? '',
        class_subject: c.class_subject || c.subject || null,
        image: normalizedImg,
      };
    };

    const relatedChapters = Array.isArray(rawRelated) ? rawRelated.map(mapChapter).filter(x => x !== null) : [];

    return {
      success: true,
      data: transformedResource,
      resource: transformedResource, // alias for backward compatibility
      ...additionalData,
      // Provide normalized related chapters under both snake_case and camelCase keys
      related_chapters: relatedChapters,
      relatedChapters: relatedChapters,
      meta: {
        ...additionalData,
        pages: payload.pages || 1
      },
      // Include raw response for debugging
      _raw: payload
    };
  } catch (err) {
    console.error('apiv2.fetchParticularResource error:', err);
    
    // Enhanced error handling
    const enhancedError = new Error(
      err.message || `Failed to fetch resource with alias: ${params.alias}`
    );
    enhancedError.originalError = err;
    enhancedError.params = params;
    
    throw enhancedError;
  }
}

export { getAuthToken, isUserAuthenticated };
